<template>
    <div class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
            <div class="modal-header">
                <h2>成长基金变动记录</h2>
                <button class="close-button" @click="closeModal">&times;</button>
            </div>
            <div class="modal-body">
                <div class="tabs">
                    <button :class="{ active: activeTab === 'history' }" @click="activeTab = 'history'"
                        class="tab-button">
                        变动记录
                    </button>
                    <button :class="{ active: activeTab === 'analysis' }" @click="activeTab = 'analysis'"
                        class="tab-button">
                        任务分析
                    </button>
                    <button :class="{ active: activeTab === 'countTrend' }" @click="activeTab = 'countTrend'"
                        class="tab-button">
                        计数趋势
                    </button>
                </div>

                <div v-show="activeTab === 'history'">
                    <div v-if="worthHistory.length === 0" class="empty-state">
                        暂无价值变动记录
                    </div>
                    <div v-else class="history-list">
                        <div v-for="record in worthHistory" :key="record.id" class="history-item">
                            <div class="history-info">
                                <span class="reason">{{ record.worth_reason }}</span>
                                <span class="time">{{ formatTime(record.worth_time) }}</span>
                            </div>
                            <div class="history-value" :class="record.worth_type">
                                {{ record.worth_type === 'add' ? '+' : '' }}{{ record.worth_change }}
                            </div>
                        </div>
                    </div>
                </div>

                <div v-show="activeTab === 'analysis'" class="analysis-content">
                    <div ref="chartContainer" class="chart-container"></div>
                    <div v-if="chartData.length === 0" class="empty-chart">
                        暂无数据分析
                    </div>
                </div>

                <div v-show="activeTab === 'countTrend'" class="count-trend-content">
                    <div class="count-trend-controls">
                        <div class="time-dimension-selector">
                            <label>时间维度：</label>
                            <select v-model="countTrendDimension" @change="loadCountTrendData">
                                <option value="day">日</option>
                                <option value="week">周</option>
                                <option value="month">月</option>
                                <option value="year">年</option>
                            </select>
                        </div>
                        <div class="display-type-selector">
                            <label>显示类型：</label>
                            <select v-model="countTrendDisplayType" @change="loadCountTrendData">
                                <option value="increment">变动量</option>
                                <option value="cumulative">累计量</option>
                            </select>
                        </div>
                        <div class="navigation-buttons">
                            <button @click="navigateCountTrend(-1)">←</button>
                            <button @click="navigateCountTrend(1)">→</button>
                        </div>
                    </div>
                    <div ref="countTrendChartContainer" class="chart-container"></div>
                    <div v-if="countTrendData.length === 0" class="empty-chart">
                        暂无计数趋势数据
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import * as echarts from 'echarts'
import { useTodoStore } from '../stores/todoStore.js'
import TodoService from '@/services/todoService.js'

export default {
    name: 'WorthHistoryModal',
    setup() {
        const store = useTodoStore()
        const activeTab = ref('history')
        const chartContainer = ref(null)
        const countTrendChartContainer = ref(null)
        let chartInstance = null
        let countTrendChartInstance = null
        const countTrendData = ref([])
        const countTrendDimension = ref('day')
        const countTrendDisplayType = ref('increment') // 'increment' 或 'cumulative'
        const currentDateRange = ref({
            start: new Date(),
            end: new Date()
        })

        const worthHistory = computed(() => store.worthHistory)

        // 计算图表数据
        const chartData = computed(() => {
            if (!worthHistory.value.length || !store.tasks.length || !store.types.length) {
                return []
            }

            // 创建类型ID到标题的映射
            const typeMap = {}
            store.types.forEach(type => {
                typeMap[type.id] = type.name
            })

            // 创建任务ID到类型ID的映射
            const taskTypeMap = {}
            store.tasks.forEach(task => {
                taskTypeMap[task.id] = task.typeId
            })

            // 按类型聚合价值变动
            const typeValues = {}
            worthHistory.value.forEach(record => {
                // 遍历关联的任务项
                if (record.todo_ids && record.todo_ids.length > 0) {
                    record.todo_ids.forEach(todoId => {
                        const typeId = taskTypeMap[todoId]
                        if (typeId) {
                            if (!typeValues[typeId]) {
                                typeValues[typeId] = 0
                            }
                            // 根据变动类型处理值
                            const value = parseFloat(record.worth_change) || 0
                            // 对于增加类型，直接加；对于扣除类型，减去绝对值
                            typeValues[typeId] += record.worth_type === 'add' ? value : -Math.abs(value)
                        }
                    })
                }
            })

            // 转换为图表所需格式
            return Object.keys(typeValues).map(typeId => ({
                name: typeMap[typeId] || `类型${typeId}`,
                value: Math.abs(typeValues[typeId]) // 使用绝对值显示
            })).filter(item => item.value > 0) // 只显示有值的项
        })

        const closeModal = () => {
            store.closeWorthHistoryModal()
        }

        const formatTime = (timeString) => {
            if (!timeString) return ''
            const date = new Date(timeString)
            return date.toLocaleString('zh-CN')
        }

        // 初始化图表
        const initChart = () => {
            console.log('Initializing chart, container exists:', !!chartContainer.value)
            if (chartContainer.value) {
                console.log('Chart container dimensions:', chartContainer.value.offsetWidth, 'x', chartContainer.value.offsetHeight)
                try {
                    if (chartInstance) {
                        chartInstance.dispose()
                    }
                } catch (e) {
                    console.warn('Error disposing existing chart:', e)
                }

                try {
                    chartInstance = echarts.init(chartContainer.value)
                    console.log('Chart initialized successfully')
                    updateChart()
                } catch (e) {
                    console.error('Error initializing chart:', e)
                }
            } else {
                console.warn('Chart container not found')
            }
        }

        // 初始化计数趋势图表
        const initCountTrendChart = () => {
            console.log('Initializing count trend chart, container exists:', !!countTrendChartContainer.value)
            if (countTrendChartContainer.value) {
                console.log('Count trend chart container dimensions:', countTrendChartContainer.value.offsetWidth, 'x', countTrendChartContainer.value.offsetHeight)
                try {
                    if (countTrendChartInstance) {
                        countTrendChartInstance.dispose()
                    }
                } catch (e) {
                    console.warn('Error disposing existing count trend chart:', e)
                }

                try {
                    countTrendChartInstance = echarts.init(countTrendChartContainer.value)
                    console.log('Count trend chart initialized successfully')
                    updateCountTrendChart()
                } catch (e) {
                    console.error('Error initializing count trend chart:', e)
                }
            } else {
                console.warn('Count trend chart container not found')
            }
        }

        // 更新图表
        const updateChart = () => {
            console.log('Updating chart, instance exists:', !!chartInstance)
            if (!chartInstance) return

            try {
                const option = {
                    title: {
                        text: '任务价值分布',
                        left: 'center'
                    },
                    tooltip: {
                        trigger: 'item',
                        formatter: '{a} <br/>{b}: {c} ({d}%)'
                    },
                    legend: {
                        orient: 'vertical',
                        left: 'left'
                    },
                    series: [
                        {
                            name: '价值分布',
                            type: 'pie',
                            radius: '50%',
                            data: chartData.value,
                            emphasis: {
                                itemStyle: {
                                    shadowBlur: 10,
                                    shadowOffsetX: 0,
                                    shadowColor: 'rgba(0, 0, 0, 0.5)'
                                }
                            }
                        }
                    ]
                }

                chartInstance.setOption(option, { notMerge: true })
                console.log('Chart updated successfully')
            } catch (e) {
                console.error('Error updating chart:', e)
            }
        }

        // 更新计数趋势图表
        const updateCountTrendChart = () => {
            console.log('Updating count trend chart, instance exists:', !!countTrendChartInstance)
            if (!countTrendChartInstance) return

            try {
                // 准备图表数据
                const seriesData = countTrendData.value.map(task => ({
                    name: task.task,
                    type: 'line',
                    data: task.stats.map(stat => [stat.period, stat.value]),
                    smooth: true
                }))

                // 获取所有唯一的时期
                const allPeriods = [...new Set(countTrendData.value.flatMap(task => task.stats.map(stat => stat.period)))].sort()

                // 根据显示类型设置Y轴名称
                const yAxisName = countTrendDisplayType.value === 'increment' ? '当日增量' : '累计进度'

                const option = {
                    title: {
                        text: '计数任务趋势',
                        left: 'center'
                    },
                    tooltip: {
                        trigger: 'axis'
                    },
                    legend: {
                        data: countTrendData.value.map(task => task.task),
                        bottom: 10
                    },
                    xAxis: {
                        type: 'category',
                        data: allPeriods
                    },
                    yAxis: {
                        type: 'value',
                        name: yAxisName
                    },
                    series: seriesData
                }

                countTrendChartInstance.setOption(option, { notMerge: true })
                console.log('Count trend chart updated successfully')
            } catch (e) {
                console.error('Error updating count trend chart:', e)
            }
        }

        // 强制刷新图表
        const refreshChart = () => {
            if (activeTab.value === 'analysis' && chartContainer.value) {
                try {
                    if (chartInstance) {
                        chartInstance.dispose()
                        chartInstance = null
                    }
                } catch (e) {
                    console.warn('Error disposing chart in refresh:', e)
                }
                initChart()
            }
        }

        // 加载计数趋势数据
        const loadCountTrendData = async () => {
            try {
                // 计算日期范围
                const startDate = new Date(currentDateRange.value.start);
                const endDate = new Date(currentDateRange.value.end);

                // 格式化日期为ISO字符串
                const startDateStr = startDate.toISOString();
                const endDateStr = endDate.toISOString();

                const response = await TodoService.getCountTaskStats({
                    start_date: startDateStr,
                    end_date: endDateStr,
                    dimension: countTrendDimension.value,
                    display_type: countTrendDisplayType.value
                });

                if (response.status === 'ok') {
                    countTrendData.value = response.statistics;
                    // 更新图表
                    if (activeTab.value === 'countTrend') {
                        nextTick(() => {
                            if (!countTrendChartInstance) {
                                initCountTrendChart();
                            } else {
                                updateCountTrendChart();
                            }
                        });
                    }
                } else {
                    console.error('获取计数趋势数据失败:', response.message);
                    countTrendData.value = [];
                }
            } catch (error) {
                console.error('获取计数趋势数据异常:', error);
                countTrendData.value = [];
            }
        }

        // 导航计数趋势数据
        const navigateCountTrend = (direction) => {
            const startDate = new Date(currentDateRange.value.start);
            const endDate = new Date(currentDateRange.value.end);

            // 根据时间维度调整日期范围
            switch (countTrendDimension.value) {
                case 'day':
                    startDate.setDate(startDate.getDate() + direction);
                    endDate.setDate(endDate.getDate() + direction);
                    break;
                case 'week':
                    startDate.setDate(startDate.getDate() + direction * 7);
                    endDate.setDate(endDate.getDate() + direction * 7);
                    break;
                case 'month':
                    startDate.setMonth(startDate.getMonth() + direction);
                    endDate.setMonth(endDate.getMonth() + direction);
                    break;
                case 'year':
                    startDate.setFullYear(startDate.getFullYear() + direction);
                    endDate.setFullYear(endDate.getFullYear() + direction);
                    break;
            }

            currentDateRange.value.start = startDate;
            currentDateRange.value.end = endDate;

            // 重新加载数据
            loadCountTrendData();
        }

        // 监听标签页切换
        watch(activeTab, (newTab) => {
            console.log('Tab switched to:', newTab)
            if (newTab === 'analysis') {
                console.log('Switching to analysis tab')
                nextTick(() => {
                    // 确保DOM已经更新
                    console.log('Next tick executed, container exists:', !!chartContainer.value)
                    if (chartContainer.value) {
                        console.log('Container dimensions:', chartContainer.value.offsetWidth, 'x', chartContainer.value.offsetHeight)
                        // 延迟一小段时间确保DOM完全渲染
                        setTimeout(() => {
                            console.log('Delayed init after 50ms')
                            // 如果图表实例不存在，重新初始化
                            if (!chartInstance) {
                                console.log('Chart instance not found, initializing')
                                initChart()
                            } else {
                                // 如果图表实例存在，确保其大小正确并更新数据
                                try {
                                    console.log('Chart instance exists, resizing and updating')
                                    chartInstance.resize()
                                    updateChart()
                                } catch (e) {
                                    console.warn('Error resizing/updating chart:', e)
                                    // 如果出现错误，重新初始化图表
                                    console.log('Error occurred, reinitializing chart')
                                    initChart()
                                }
                            }
                        }, 50)
                    } else {
                        console.warn('Chart container not found when switching to analysis tab')
                    }
                })
            } else if (newTab === 'countTrend') {
                console.log('Switching to count trend tab')
                // 初始化默认日期范围
                const now = new Date();
                currentDateRange.value.start = new Date(now.getFullYear(), now.getMonth(), 1); // 本月第一天
                currentDateRange.value.end = new Date(now.getFullYear(), now.getMonth() + 1, 0); // 本月最后一天

                nextTick(() => {
                    // 确保DOM已经更新
                    console.log('Next tick executed, count trend container exists:', !!countTrendChartContainer.value)
                    if (countTrendChartContainer.value) {
                        console.log('Count trend container dimensions:', countTrendChartContainer.value.offsetWidth, 'x', countTrendChartContainer.value.offsetHeight)
                        // 延迟一小段时间确保DOM完全渲染
                        setTimeout(() => {
                            console.log('Delayed init after 50ms')
                            // 如果图表实例不存在，重新初始化
                            if (!countTrendChartInstance) {
                                console.log('Count trend chart instance not found, initializing')
                                initCountTrendChart()
                            } else {
                                // 如果图表实例存在，确保其大小正确
                                try {
                                    console.log('Count trend chart instance exists, resizing')
                                    countTrendChartInstance.resize()
                                } catch (e) {
                                    console.warn('Error resizing count trend chart:', e)
                                }
                            }

                            // 加载数据
                            loadCountTrendData();
                        }, 50)
                    } else {
                        console.warn('Count trend chart container not found when switching to count trend tab')
                    }
                })
            } else if (newTab === 'history' && (chartInstance || countTrendChartInstance)) {
                console.log('Switching to history tab')
                // 当切换到历史记录标签页时，确保图表被正确清理
                try {
                    if (chartInstance) {
                        chartInstance.resize() // 调整大小以适应隐藏状态
                    }
                    if (countTrendChartInstance) {
                        countTrendChartInstance.resize() // 调整大小以适应隐藏状态
                    }
                } catch (e) {
                    console.warn('Error resizing chart when switching to history tab:', e)
                }
            }
        })

        // 监听数据变化，更新图表
        watch(chartData, () => {
            console.log('Chart data changed, active tab:', activeTab.value)
            if (activeTab.value === 'analysis') {
                nextTick(() => {
                    // 确保DOM已经更新
                    console.log('Next tick in chart data watcher, container exists:', !!chartContainer.value)
                    if (chartContainer.value) {
                        // 如果图表实例不存在，重新初始化
                        if (!chartInstance) {
                            console.log('Chart instance not found in data watcher, initializing')
                            initChart()
                        } else {
                            // 如果图表实例存在，更新数据
                            console.log('Chart instance exists in data watcher, updating')
                            updateChart()
                        }
                    }
                })
            }
        })

        // 监听计数趋势数据变化，更新图表
        watch(countTrendData, () => {
            console.log('Count trend data changed, active tab:', activeTab.value)
            if (activeTab.value === 'countTrend') {
                nextTick(() => {
                    // 确保DOM已经更新
                    console.log('Next tick in count trend data watcher, container exists:', !!countTrendChartContainer.value)
                    if (countTrendChartContainer.value) {
                        // 如果图表实例不存在，重新初始化
                        if (!countTrendChartInstance) {
                            console.log('Count trend chart instance not found in data watcher, initializing')
                            initCountTrendChart()
                        } else {
                            // 如果图表实例存在，更新数据
                            console.log('Count trend chart instance exists in data watcher, updating')
                            updateCountTrendChart()
                        }
                    }
                })
            }
        })

        // 组件挂载时初始化
        onMounted(() => {
            console.log('Component mounted')
            nextTick(() => {
                console.log('Next tick in mounted hook, active tab:', activeTab.value)
                // 延迟初始化以确保DOM完全准备好
                setTimeout(() => {
                    console.log('Delayed init in mounted hook, container exists:', !!chartContainer.value)
                    if (activeTab.value === 'analysis' && chartContainer.value) {
                        console.log('Initializing chart on mount')
                        initChart()
                    }
                }, 100)

                // 监听窗口大小变化
                if (typeof window !== 'undefined') {
                    window.addEventListener('resize', () => {
                        if (chartInstance && chartContainer.value) {
                            chartInstance.resize()
                        }
                        if (countTrendChartInstance && countTrendChartContainer.value) {
                            countTrendChartInstance.resize()
                        }
                    })
                }
            })
        })

        // 组件卸载时清理
        onUnmounted(() => {
            if (chartInstance) {
                try {
                    chartInstance.dispose()
                } catch (e) {
                    console.warn('Error disposing chart:', e)
                }
                chartInstance = null
            }
            if (countTrendChartInstance) {
                try {
                    countTrendChartInstance.dispose()
                } catch (e) {
                    console.warn('Error disposing count trend chart:', e)
                }
                countTrendChartInstance = null
            }
        })

        return {
            worthHistory,
            chartData,
            chartContainer,
            countTrendChartContainer,
            countTrendData,
            countTrendDimension,
            countTrendDisplayType,
            activeTab,
            closeModal,
            formatTime,
            refreshChart,
            loadCountTrendData,
            navigateCountTrend
        }
    }
}
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    width: 90%;
    max-width: 800px;
    max-height: 80vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.modal-header {
    padding: 20px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-header h2 {
    margin: 0;
    font-size: 1.5rem;
    color: #333;
}

.close-button {
    background: none;
    border: none;
    font-size: 1.8rem;
    cursor: pointer;
    color: #999;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-button:hover {
    color: #333;
}

.tabs {
    display: flex;
    border-bottom: 1px solid #eee;
}

.tab-button {
    padding: 12px 20px;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 1rem;
    color: #666;
    border-bottom: 2px solid transparent;
}

.tab-button:hover {
    background-color: #f5f5f5;
}

.tab-button.active {
    color: #409eff;
    border-bottom-color: #409eff;
}

.modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
}

.empty-state {
    text-align: center;
    color: #999;
    padding: 40px 20px;
}

.history-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.history-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border-radius: 6px;
    background-color: #f8f9fa;
    transition: background-color 0.2s;
}

.history-item:hover {
    background-color: #e9ecef;
}

.history-info {
    flex: 1;
}

.reason {
    display: block;
    font-weight: 500;
    color: #333;
    margin-bottom: 4px;
}

.time {
    display: block;
    font-size: 0.85rem;
    color: #666;
}

.history-value {
    font-weight: bold;
    font-size: 1.1rem;
    padding: 6px 12px;
    border-radius: 4px;
}

.history-value.add {
    background-color: #d4edda;
    color: #155724;
}

.history-value.subtract {
    background-color: #f8d7da;
    color: #721c24;
}

.analysis-content {
    min-height: 400px;
}

.chart-container {
    width: 100%;
    height: 400px;
    /* 确保容器在隐藏时也能保持尺寸 */
    min-width: 200px;
    min-height: 200px;
}

.count-trend-content {
    min-height: 400px;
}

.count-trend-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 10px;
    background-color: #f5f5f5;
    border-radius: 4px;
    flex-wrap: wrap;
    gap: 10px;
}

.time-dimension-selector,
.display-type-selector {
    display: flex;
    align-items: center;
    gap: 10px;
}

.time-dimension-selector label,
.display-type-selector label {
    font-weight: bold;
}

.time-dimension-selector select,
.display-type-selector select {
    padding: 5px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: white;
}

.navigation-buttons {
    display: flex;
    gap: 10px;
}

.navigation-buttons button {
    padding: 8px 15px;
    background-color: #409eff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: bold;
}

.navigation-buttons button:hover {
    background-color: #337ecc;
}

.empty-chart {
    text-align: center;
    color: #999;
    padding: 40px 20px;
}
</style>